import React, { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import ChatFAB from "./ChatFAB";
import ChatHeader from "./ChatHeader";
import ChatMessageList from "./ChatMessageList";
import ChatInputBar from "./ChatInputBar";
import CallStatusPanel from "./CallStatusPanel";
import type { ICallState, IChatMessage } from "../../store/@types/chat";
import { INITIAL_MESSAGES, toHistory } from "../../constants/aiData";
import {
  aiErrorMessage,
  mint_live_token,
  send_site_chat,
} from "../../service/api/ai";
import {
  GeminiLiveCall,
  isSecureAudioContext,
} from "../../utils/geminiLiveCall";

type IChatWidget = {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
};

const ChatWidget: React.FC<IChatWidget> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [callState, setCallState] = useState<ICallState>("idle");
  const [messages, setMessages] = useState<IChatMessage[]>(INITIAL_MESSAGES);
  const [busy, setBusy] = useState(false);
  const liveCall = useRef(new GeminiLiveCall());
  const startingCall = useRef(false);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const pushMessage = (role: IChatMessage["role"], content: string) => {
    const next: IChatMessage = {
      id: `${Date.now()}-${role}`,
      role,
      content,
    };
    setMessages((prev) => [...prev, next]);
  };

  const handleSendMessage = async (content: string) => {
    if (busy || callState !== "idle") return;

    const userMessage: IChatMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      content,
    };
    const previous = messages;
    setMessages([...previous, userMessage]);
    setBusy(true);

    try {
      const reply = await send_site_chat(content, toHistory(previous));
      pushMessage("assistant", reply);
    } catch (error) {
      pushMessage(
        "assistant",
        aiErrorMessage(error, "The assistant could not reply right now."),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleEndCall = () => {
    liveCall.current.stop();
    setCallState("idle");
  };

  const handleStartCall = async () => {
    if (callState !== "idle" || startingCall.current) return;

    if (!isSecureAudioContext()) {
      pushMessage(
        "assistant",
        "Voice call requires HTTPS (or localhost). Open the site securely and try again.",
      );
      return;
    }

    if (!window.WebSocket || !navigator.mediaDevices?.getUserMedia) {
      pushMessage(
        "assistant",
        "This browser cannot start a live audio call. Try Chrome or Edge on HTTPS.",
      );
      return;
    }

    startingCall.current = true;
    setCallState("connecting");
    setIsOpen(true);

    try {
      const session = await mint_live_token();
      await liveCall.current.start(session, {
        onStatus: (status) => {
          setCallState(status);
        },
        onUnexpectedEnd: () => {
          setCallState("idle");
          pushMessage(
            "assistant",
            "The live call ended unexpectedly. Please try again.",
          );
        },
      });
      setCallState("listening");
    } catch (error) {
      liveCall.current.stop();
      setCallState("idle");
      pushMessage(
        "assistant",
        aiErrorMessage(error, "Could not start the live call."),
      );
    } finally {
      startingCall.current = false;
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code === "Space" && callState !== "idle") {
        const target = event.target as HTMLElement | null;
        if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
        event.preventDefault();
        liveCall.current.interrupt();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [callState]);

  useEffect(
    () => () => {
      liveCall.current.stop();
    },
    [],
  );

  return (
    <>
      <ChatFAB callState={callState} onClick={handleToggle} />

      {isOpen && (
        <div
          className={twMerge(
            "fixed inset-x-0 bottom-0 z-40 flex max-h-[70vh] w-full flex-col overflow-hidden border border-white bg-white shadow-2xl shadow-slate-900/20 sm:inset-x-auto sm:bottom-14 sm:right-28 sm:w-96 sm:rounded-2xl",
          )}
        >
          <ChatHeader
            callState={callState}
            onStartCall={() => {
              void handleStartCall();
            }}
            onEndCall={handleEndCall}
            onClose={() => setIsOpen(false)}
          />

          {callState === "idle" ? (
            <>
              <ChatMessageList messages={messages} />
              <ChatInputBar
                onSend={(content) => void handleSendMessage(content)}
                disabled={busy}
              />
            </>
          ) : (
            <CallStatusPanel callState={callState} />
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
